import { useEffect, useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { Loader2, ShieldAlert, ArrowLeft } from "lucide-react";
import { getSupabase } from "@/lib/supabase";

/**
 * RequireSuperAdm — guarda de rota do Console ADM (/admin).
 *
 * Regra (Jun/2026): o painel só renderiza para uma sessão Supabase VÁLIDA
 * cujo usuário tenha papel super_adm. A autorização NÃO confia no
 * `app_session` do localStorage (forjável). Em vez disso:
 *   1. lê a sessão viva via supabase.auth.getSession() (auto-refresh);
 *   2. revalida no backend GET /api/auth/me com o access_token — a flag
 *      `is_super_adm` vem de adm.usuarios (fonte de verdade no servidor).
 *
 * Sem sessão → volta ao login ("/"). Com sessão mas sem papel → tela de
 * acesso negado (nunca monta o painel). Falha de rede/5xx → fail closed.
 */
export default function RequireSuperAdm({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | allowed | no-session | forbidden

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data } = await sb.auth.getSession();
        let token = data?.session?.access_token;
        if (!token) {
          try {
            const { data: r } = await sb.auth.refreshSession();
            token = r?.session?.access_token;
          } catch { /* sem refresh token válido */ }
        }
        if (!token) { if (!cancelled) setStatus("no-session"); return; }

        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${base}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 401) { setStatus("no-session"); return; }
        if (!res.ok) { setStatus("forbidden"); return; } // fail closed
        const me = await res.json();
        setStatus(me?.is_super_adm === true ? "allowed" : "forbidden");
      } catch {
        if (!cancelled) setStatus("no-session");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 text-slate-300"
           data-testid="admin-guard-checking" aria-busy="true">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-admin-400" />
          <span className="text-sm text-slate-400">Verificando autorização…</span>
        </div>
      </div>
    );
  }

  if (status === "no-session") {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (status === "forbidden") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 px-6" data-testid="admin-guard-forbidden">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-md bg-admin-500/10 border border-admin-500/30 grid place-items-center mx-auto text-admin-300">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <h1 className="font-display text-xl font-medium text-white">Acesso restrito</h1>
          <p className="text-sm text-slate-400">
            Esta área é exclusiva para administradores da plataforma. Sua conta não tem o papel necessário.
          </p>
          <Link to="/app/dashboard"
                className="inline-flex items-center gap-1.5 text-sm text-admin-300 hover:text-admin-200"
                data-testid="admin-guard-back">
            <ArrowLeft className="w-4 h-4" /> Voltar à sua área de trabalho
          </Link>
        </div>
      </div>
    );
  }

  return children;
}
